import type { Adjacency } from './types';

/**
 * Build triangle adjacency from an index buffer (run on WELDED indices so shared
 * edges actually match). `adjacent[t*3 + s]` is the triangle across edge slot `s`
 * (s=0:v0v1, 1:v1v2, 2:v2v0), or -1 for a boundary / non-manifold edge.
 *
 * Edge key = lo*vertexCount + hi, exact for vertexCount < 2^26 (~67M) within
 * Number's 2^53 safe-integer range. Non-manifold edges (>2 triangles) link only
 * the first pair; extras become boundaries.
 */
export function buildAdjacency(indices: Uint32Array, vertexCount: number): Adjacency {
  const triCount = indices.length / 3;
  const adjacent = new Int32Array(triCount * 3).fill(-1);
  const edgeMap = new Map<number, number>(); // edge key -> (tri*3 + slot)

  const edgeKey = (a: number, b: number) => (a < b ? a * vertexCount + b : b * vertexCount + a);

  for (let t = 0; t < triCount; t++) {
    const i0 = indices[t * 3];
    const i1 = indices[t * 3 + 1];
    const i2 = indices[t * 3 + 2];
    // Skip degenerate triangles (repeated index — typical for slivers whose verts
    // just got welded together). Registering their edges is actively harmful: a
    // (a,b,a) sliver pairs edge (a,b) with ITSELF, consuming the map entry so the
    // two REAL triangles sharing that edge never link — a false BFS wall exactly
    // along a weld seam. Degenerates stay boundary-only (-1 all around).
    if (i0 === i1 || i1 === i2 || i2 === i0) continue;
    for (let s = 0; s < 3; s++) {
      // Edge slot s: (v0v1, v1v2, v2v0). Inlined — no per-triangle array allocs
      // (this loop runs once per triangle, i.e. millions of times on dense scans).
      const a = s === 0 ? i0 : s === 1 ? i1 : i2;
      const b = s === 0 ? i1 : s === 1 ? i2 : i0;
      const key = edgeKey(a, b);
      const prev = edgeMap.get(key);
      if (prev === undefined) {
        edgeMap.set(key, t * 3 + s);
      } else {
        const pt = (prev / 3) | 0;
        const ps = prev % 3;
        // Link the first unpaired match only; ignore further (non-manifold) edges.
        if (adjacent[t * 3 + s] === -1 && adjacent[pt * 3 + ps] === -1) {
          adjacent[t * 3 + s] = pt;
          adjacent[pt * 3 + ps] = t;
        }
      }
    }
  }

  return { adjacent };
}
